import { BookingHour } from "@/data";
import { FBT } from "@/lib/i18n/i18n_provider";

export function getOperatingHours(
  bookingHours: BookingHour[],
  fbt: FBT
): ReadableOperatingHours[] {
  const sortedDays = sortDays(bookingHours);
  const compactedOperatingHours = compactOperatingHours(sortedDays);
  const operatingHours = readableOperatingHours(compactedOperatingHours, fbt);
  return operatingHours;
}

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function sortDays(bookingHours: BookingHour[]): BookingHour[] {
  return bookingHours
    .slice(0)
    .sort((a: BookingHour, b: BookingHour): number => {
      return a.dayOfWeek - b.dayOfWeek;
    });
}

interface ReadableOperatingHours {
  label: string;
  open: string;
  close: string;
}

function readableOperatingHours(
  operatingHours: CompactOperatingHours[],
  fbt: FBT
): ReadableOperatingHours[] {
  const result: ReadableOperatingHours[] = [];
  operatingHours.forEach((operatingHour) => {
    if (
      operatingHour.startDayLabel === operatingHour.endDayLabel &&
      operatingHour.open === operatingHour.close
    ) {
      return;
    }

    const startDayPrefix = operatingHour.startDayLabel.slice(0, 3);
    const endDayPrefix = operatingHour.endDayLabel.slice(0, 3);

    const openHour = Number(operatingHour.open.slice(0, 2));
    const openMinute = operatingHour.open.slice(2, 4);
    const openMeridiem = openHour < 12 ? fbt("AM") : fbt("PM");
    const meridiemOpenHour =
      openMeridiem === fbt("AM") || openHour === 12
        ? openHour === 0
          ? 12
          : openHour
        : openHour % 12;

    const closeHour = Number(operatingHour.close.slice(0, 2));
    const closeMinute = operatingHour.close.slice(2, 4);
    const closeMeridiem = closeHour < 12 ? fbt("AM") : fbt("PM");
    const meridiemCloseHour =
      closeMeridiem === fbt("AM") || closeHour === 12
        ? closeHour === 0
          ? 12
          : closeHour
        : closeHour % 12;

    result.push({
      label:
        startDayPrefix === endDayPrefix
          ? fbt(startDayPrefix)
          : `${fbt(startDayPrefix)} - ${fbt(endDayPrefix)}`,
      open: `${meridiemOpenHour}${
        Number(openMinute) === 0 ? "" : `:${openMinute}`
      } ${openMeridiem}`,
      close: `${meridiemCloseHour}${
        Number(closeMinute) === 0 ? "" : `:${closeMinute}`
      } ${closeMeridiem}`,
    });
  });
  return result;
}

interface CompactOperatingHours {
  startDayLabel: string;
  endDayLabel: string;
  open: string;
  close: string;
}

function compactOperatingHours(
  bookingHours: BookingHour[]
): CompactOperatingHours[] {
  const result: CompactOperatingHours[] = [];

  if (!hasSameOpeningHourWithoutLastItem(bookingHours)) {
    return bookingHours.map((bookingHour) => ({
      startDayLabel: getDayName(bookingHour.dayOfWeek),
      endDayLabel: getDayName(bookingHour.dayOfWeek),
      open: bookingHour.openAt,
      close: bookingHour.closedAt,
    }));
  }

  let startDay: string = "";
  let endDay: string = "";
  let prevDayOpen: string = "";
  let prevDayClose: string = "";
  bookingHours.forEach((bookingHour, i) => {
    if (!startDay) {
      startDay = getDayName(bookingHour.dayOfWeek);
      endDay = getDayName(bookingHour.dayOfWeek);
      prevDayOpen = bookingHour.openAt;
      prevDayClose = bookingHour.closedAt;
      if (i === bookingHours.length - 1) {
        result.push({
          startDayLabel: startDay,
          endDayLabel: endDay,
          open: prevDayOpen,
          close: prevDayClose,
        });
      }
      return;
    }

    if (
      prevDayOpen === bookingHour.openAt &&
      prevDayClose === bookingHour.closedAt
    ) {
      endDay = getDayName(bookingHour.dayOfWeek);
      if (i === bookingHours.length - 1) {
        result.push({
          startDayLabel: startDay,
          endDayLabel: endDay,
          open: prevDayOpen,
          close: prevDayClose,
        });
      }
      return;
    }

    result.push({
      startDayLabel: startDay,
      endDayLabel: endDay,
      open: prevDayOpen,
      close: prevDayClose,
    });
    startDay = getDayName(bookingHour.dayOfWeek);
    endDay = getDayName(bookingHour.dayOfWeek);
    prevDayOpen = bookingHour.openAt;
    prevDayClose = bookingHour.closedAt;

    if (i === bookingHours.length - 1) {
      result.push({
        startDayLabel: startDay,
        endDayLabel: endDay,
        open: prevDayOpen,
        close: prevDayClose,
      });
    }
  });

  return result;
}

const hasSameOpeningHourWithoutLastItem = (
  bookingHours: BookingHour[]
): boolean => {
  return bookingHours.every((bookingHour, ind) => {
    return (
      bookingHour.openAt === bookingHours[0].openAt ||
      ind === bookingHours.length - 1
    );
  });
};

const getDayName = (day: number): string => {
  return days[day];
};
